import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LinkButton from 'flarum/common/components/LinkButton';
import extractText from 'flarum/common/utils/extractText';
import Stream from 'flarum/common/utils/Stream';
import app from 'flarum/forum/app';
import { formatDateTime } from '../utils/format';
import { ticketUrl } from '../utils/ticketUrl';
import { attachmentError, attachmentUrl, isImageAttachment, pastedImage, uploadAttachment } from '../utils/attachments';
const T = (key, params) => app.translator.trans('cengizkemec-support.forum.' + key, params);
const STATUSES = ['new', 'answered', 'pending', 'resolved', 'closed'];

/**
 * Link to a user's profile so nicknames inside a ticket are clickable.
 */
function userHref(user) {
  return app.route('user', {
    username: user.slug ? user.slug() : user.username()
  });
}
export default class TicketPage extends Page {
  loading = true;
  error = null;
  ticket = null;
  replyMessage = Stream('');
  attachment = Stream('');
  attachmentName = Stream('');
  /** Local object URL used to preview the file before it is submitted. */
  preview = Stream('');
  submitting = false;
  uploading = false;
  oninit(vnode) {
    super.oninit(vnode);

    // Never leave the previous page's title in the tab while loading.
    app.setTitle(extractText(T('meta_title_text')));
    const id = m.route.param('id');
    this.load(id);
  }
  load(id) {
    app.store.find('tickets', id, {
      include: 'replies,replies.user,category,assignee,user'
    }).then(ticket => {
      this.ticket = ticket;
      this.loading = false;
      app.setTitle(extractText(ticket.subject() || T('meta_title_text')));

      // Upgrade /support/18 to the readable /support/18/ticket-subject form.
      // Guarded on a missing slug so it can only ever happen once and can
      // never bounce back and forth.
      if (!m.route.param('slug') && ticket.slug && ticket.slug()) {
        m.route.set(ticketUrl(ticket), {}, {
          replace: true
        });
      }
      m.redraw();
    }).catch(e => {
      this.loading = false;
      this.error = e?.response?.errors?.[0]?.detail || e?.message || T('error_load');
      m.redraw();
    });
  }
  view() {
    if (this.loading) {
      return m(PageStructure, {
        className: "TicketPage"
      }, m("div", {
        className: "SupportLoading"
      }, m(LoadingIndicator, null)));
    }
    if (this.error || !this.ticket) {
      return m(PageStructure, {
        className: "TicketPage"
      }, m("div", {
        className: "SupportEmpty"
      }, m("div", {
        className: "SupportEmpty-icon"
      }, m("i", {
        className: "icon fas fa-triangle-exclamation"
      })), m("div", {
        className: "SupportEmpty-title"
      }, this.error || T('error_not_found'))));
    }
    const ticket = this.ticket;
    const status = ticket.status();
    const isStaff = !!app.forum.attribute('canSupportViewAll') || !!app.forum.attribute('canSupportManage');
    const isClosed = status === 'closed';
    const canReply = (!isClosed || isStaff) && !!app.session.user;
    return m(PageStructure, {
      className: "TicketPage",
      hero: () => m("div", {
        className: "container"
      }, m(LinkButton, {
        className: "TicketPage-back",
        href: app.route('support'),
        icon: "fas fa-arrow-left"
      }, T('back_to_support')), m("h2", {
        className: "TicketPage-hero"
      }, m("i", {
        className: "icon fas fa-ticket"
      }), " ", ticket.subject()))
    }, this.header(ticket, status), this.customFields(ticket), this.message(ticket), this.replies(ticket), canReply ? this.replyForm() : this.closedNote(), isStaff && this.staffPanel(ticket, status));
  }

  /**
   * A closed ticket cannot be replied to, so give people a way out: back to the
   * ticket list they came from.
   */
  closedNote() {
    return m("div", {
      className: "TicketClosedNote"
    }, m("p", {
      className: "TicketClosedNote-text"
    }, T('closed_note')), m(LinkButton, {
      className: "Button",
      href: app.route('support'),
      icon: "fas fa-arrow-left"
    }, T('back_to_support')));
  }

  /**
   * Render a stored attachment: images are shown inline so pasted screenshots
   * are visible straight away, everything else stays a download link.
   */
  attachmentBlock(filename) {
    if (!filename) return null;
    const url = attachmentUrl(filename);
    if (isImageAttachment(filename)) {
      return m("a", {
        className: "TicketAttachment-image",
        href: url,
        target: "_blank",
        rel: "noopener noreferrer"
      }, m("img", {
        src: url,
        alt: T('attachment'),
        loading: "lazy"
      }));
    }
    return m("a", {
      className: "TicketAttachment",
      href: url,
      target: "_blank",
      rel: "noopener noreferrer"
    }, m("i", {
      className: "icon fas fa-paperclip"
    }), " ", T('attachment'));
  }
  header(ticket, status) {
    const priority = ticket.priority();
    const category = ticket.category();
    const assignee = ticket.assignee();
    const requester = ticket.user();
    return m("div", {
      className: "TicketHeader"
    }, m("div", {
      className: "TicketHeader-meta"
    }, m("span", {
      className: 'SupportBadge SupportBadge--' + status
    }, T('status.' + status)), m("span", {
      className: 'SupportPriority SupportPriority--' + priority
    }, T('priority.' + priority)), category && m("span", {
      className: "SupportChip"
    }, m("i", {
      className: "icon fas fa-folder"
    }), " ", category.name()), m("span", {
      className: "SupportChip"
    }, m("i", {
      className: "icon far fa-clock"
    }), " ", formatDateTime(ticket.createdAt())), requester && m("span", {
      className: "SupportChip"
    }, m("i", {
      className: "icon far fa-user"
    }), ' ', m(Link, {
      className: "TicketUserLink",
      href: userHref(requester)
    }, requester.displayName())), assignee && m("span", {
      className: "SupportChip"
    }, m("i", {
      className: "icon fas fa-user-check"
    }), " ", T('assigned_to'), ":", ' ', m(Link, {
      className: "TicketUserLink",
      href: userHref(assignee)
    }, assignee.displayName()))));
  }
  message(ticket) {
    const requester = ticket.user();
    return m("div", {
      className: "TicketMessage"
    }, m("div", {
      className: "TicketAvatar"
    }, requester && m(Link, {
      href: userHref(requester)
    }, m("img", {
      className: "Avatar",
      src: requester.avatarUrl(),
      alt: requester.displayName()
    }))), m("div", {
      className: "TicketMessage-body"
    }, m("div", {
      className: "TicketMessage-head"
    }, m("span", {
      className: "TicketMessage-author"
    }, requester ? m(Link, {
      className: "TicketUserLink",
      href: userHref(requester)
    }, requester.displayName()) : T('unknown_user')), m("span", {
      className: "TicketMessage-time"
    }, formatDateTime(ticket.createdAt()))), m("div", {
      className: "TicketMessage-text"
    }, ticket.message()), this.attachmentBlock(ticket.attachment())));
  }

  /**
   * Show the answers the user gave to the admin-defined ticket form fields.
   */
  customFields(ticket) {
    const values = ticket.customFieldValues && ticket.customFieldValues() || [];
    if (!values.length) return null;
    return m("div", {
      className: "TicketFields"
    }, m("h4", {
      className: "TicketFields-title"
    }, m("i", {
      className: "icon fas fa-list-check"
    }), " ", T('details')), m("dl", {
      className: "TicketFields-list"
    }, values.map((field, index) => m("div", {
      className: "TicketField",
      key: (field.label || 'field') + index
    }, m("dt", {
      className: "TicketField-label"
    }, field.label), m("dd", {
      className: "TicketField-value"
    }, field.value)))));
  }
  replies(ticket) {
    const replies = (ticket.replies() || []).slice().sort((a, b) => {
      const da = a.createdAt() ? a.createdAt().getTime() : 0;
      const db = b.createdAt() ? b.createdAt().getTime() : 0;
      return da - db;
    });
    if (!replies.length) return null;
    return m("div", {
      className: "TicketReplies"
    }, replies.map(reply => {
      const author = reply.user();
      const staff = reply.isStaff();
      return m("div", {
        className: 'TicketReply' + (staff ? ' is-staff' : ''),
        key: reply.id()
      }, m("div", {
        className: "TicketAvatar"
      }, author && m(Link, {
        href: userHref(author)
      }, m("img", {
        className: "Avatar",
        src: author.avatarUrl(),
        alt: author.displayName()
      }))), m("div", {
        className: "TicketReply-body"
      }, m("div", {
        className: "TicketReply-head"
      }, m("span", {
        className: "TicketReply-author"
      }, author ? m(Link, {
        className: "TicketUserLink",
        href: userHref(author)
      }, author.displayName()) : T('unknown_user')), staff && m("span", {
        className: "TicketStaffTag"
      }, T('staff')), m("span", {
        className: "TicketReply-time"
      }, formatDateTime(reply.createdAt()))), m("p", {
        className: "TicketReply-text"
      }, reply.message()), this.attachmentBlock(reply.attachment())));
    }));
  }
  attachmentArea() {
    if (this.uploading) {
      return m("div", {
        className: "AttachmentRow"
      }, m("span", {
        className: "AttachmentStatus"
      }, m("i", {
        className: "icon fas fa-circle-notch fa-spin"
      }), " ", T('uploading')));
    }
    const filename = this.attachment();
    if (!filename) return null;
    const label = this.attachmentName() || T('attachment');
    return m("div", {
      className: "AttachmentRow"
    }, isImageAttachment(filename) ? m("span", {
      className: "AttachmentPreview"
    }, m("img", {
      className: "AttachmentPreview-image",
      src: this.preview() || attachmentUrl(filename),
      alt: label
    }), m("button", {
      type: "button",
      className: "AttachmentRemove",
      title: T('remove_attachment'),
      onclick: () => this.clearAttachment()
    }, m("i", {
      className: "icon fas fa-times"
    }))) : m("span", {
      className: "AttachmentChip"
    }, m("i", {
      className: "icon fas fa-paperclip"
    }), " ", label, m("button", {
      type: "button",
      className: "AttachmentRemove",
      title: T('remove_attachment'),
      onclick: () => this.clearAttachment()
    }, m("i", {
      className: "icon fas fa-times"
    }))));
  }
  replyForm() {
    return m("div", {
      className: "TicketReplyForm"
    }, m("h3", {
      className: "TicketReplyForm-title"
    }, T('reply')), m("textarea", {
      className: "FormControl",
      rows: 4,
      value: this.replyMessage(),
      oninput: e => this.replyMessage(e.target.value),
      onpaste: this.onPaste.bind(this),
      placeholder: T('reply_placeholder')
    }), m("div", {
      className: "AttachmentHint"
    }, T('attachment_hint')), this.attachmentArea(), m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-paper-plane",
      loading: this.submitting,
      disabled: this.submitting || this.uploading || !this.replyMessage(),
      onclick: this.submitReply.bind(this)
    }, T('send_reply')));
  }
  onPaste(e) {
    const file = pastedImage(e);
    if (!file) return;

    // Stop the image from being pasted as a data URL into the textarea.
    e.preventDefault();
    this.attachFromFile(file);
  }
  async attachFromFile(file) {
    if (this.uploading) return;
    const problem = attachmentError(file);
    if (problem) {
      app.alerts.show({
        type: 'error'
      }, T('upload_' + problem));
      return;
    }
    this.uploading = true;
    m.redraw();
    try {
      const response = await uploadAttachment(file);
      this.attachment(response.filename);
      this.attachmentName(response.originalName || file.name || '');
      // Show the picked image straight away; the server-side URL only becomes
      // readable once the reply is saved.
      this.setPreview(file);
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, T('upload_failed'));
    } finally {
      this.uploading = false;
      m.redraw();
    }
  }
  setPreview(file) {
    if (file.type.indexOf('image/') !== 0) return;
    this.revokePreview();
    this.preview(URL.createObjectURL(file));
  }
  revokePreview() {
    if (this.preview()) {
      URL.revokeObjectURL(this.preview());
      this.preview('');
    }
  }
  clearAttachment() {
    this.revokePreview();
    this.attachment('');
    this.attachmentName('');
    m.redraw();
  }
  staffPanel(ticket, status) {
    const staffList = app.forum.attribute('supportStaff') || [];
    const assigneeId = ticket.assignee() ? String(ticket.assignee().id()) : '';
    const isClosed = status === 'closed';
    return m("div", {
      className: "TicketStaffPanel"
    }, m("h3", {
      className: "TicketStaffPanel-title"
    }, m("i", {
      className: "icon fas fa-user-shield"
    }), " ", T('staff_panel')), m("div", {
      className: "TicketStaffGrid"
    }, m("div", {
      className: "TicketStaffField"
    }, m("label", null, T('status_label')), m("select", {
      className: "FormControl",
      value: status,
      onchange: e => this.updateStatus(e.target.value)
    }, STATUSES.map(s => m("option", {
      value: s
    }, T('status.' + s))))), m("div", {
      className: "TicketStaffField"
    }, m("label", null, T('assigned_to')), m("select", {
      className: "FormControl",
      value: assigneeId,
      onchange: e => this.assignTo(e.target.value)
    }, m("option", {
      value: ""
    }, T('unassigned')), staffList.map(u => m("option", {
      value: String(u.id)
    }, u.displayName))))), m("div", {
      className: "TicketStaffActions"
    }, isClosed ? m(Button, {
      className: "Button",
      icon: "fas fa-rotate-left",
      onclick: () => this.updateStatus('new')
    }, T('reopen')) : m("div", {
      className: "TicketStaffActions-buttons"
    }, m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-circle-check",
      onclick: () => this.updateStatus('resolved')
    }, T('mark_resolved')), m(Button, {
      className: "Button",
      icon: "fas fa-lock",
      onclick: () => this.updateStatus('closed')
    }, T('close_ticket')))));
  }
  async submitReply() {
    if (!this.replyMessage() || this.submitting || this.uploading) return;
    this.submitting = true;
    m.redraw();
    try {
      const data = {
        message: this.replyMessage(),
        relationships: {
          ticket: this.ticket
        }
      };
      if (this.attachment()) {
        data.attachment = this.attachment();
      }
      await app.store.createRecord('ticket-replies').save(data);
      this.replyMessage('');
      this.revokePreview();
      this.attachment('');
      this.attachmentName('');
      await this.load(this.ticket.id());
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, e?.response?.errors?.[0]?.detail || e?.message || T('error_reply'));
    } finally {
      this.submitting = false;
      m.redraw();
    }
  }
  async updateStatus(status) {
    const previous = this.ticket.status();
    try {
      await this.ticket.save({
        status
      });
      m.redraw();
    } catch (e) {
      this.ticket.pushData({
        attributes: {
          status: previous
        }
      });
      app.alerts.show({
        type: 'error'
      }, e?.response?.errors?.[0]?.detail || e?.message || T('error_status'));
      m.redraw();
    }
  }
  async assignTo(userId) {
    try {
      if (!userId) {
        await this.ticket.save({
          relationships: {
            assignee: null
          }
        });
      } else {
        const user = await app.store.find('users', userId);
        await this.ticket.save({
          relationships: {
            assignee: user
          }
        });
      }
      m.redraw();
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, e?.response?.errors?.[0]?.detail || e?.message || T('error_assign'));
    }
  }
}
flarum.reg.add('cengizkemec-support-tickets', 'forum/components/TicketPage', TicketPage);